/**
 * SoundService — the game's sound system (Task: sounds).
 *
 * EVERY sound the game plays goes through `play(name)` — the mute flag and
 * the volume from the in-game Settings (mute button + volume slider) are
 * enforced in ONE place here, so no call site can forget them.
 *
 * INSTANT REPLAY: each sound owns a small POOL of pre-created
 * HTMLAudioElements (created on first use, kept for the session). The
 * browser caches the .wav after the first fetch (the files live in
 * /public/Sounds), so replays after the first play start immediately —
 * the pool also lets the same sound overlap (two booms in quick
 * succession) without cutting each other off.
 */

export type SoundName =
  | 'btnClick'      // any button click in the game menus
  | 'clockTic'      // countdown timer ticks 5 → 0
  | 'explode'       // the Boom effect (Explosive/Exploding)
  | 'powerUp'       // a level-up branch selected in the upgrade menu
  | 'attack3'       // Warrior/Tank/Runekeeper/Swarmer/Undead attacks
  | 'attack2'       // Healer/Mage/Buffer/Bard/Blightcaster attacks
  | 'buyRune'       // a rune is bought
  | 'shieldImpact'  // a minion survives an attack
  | 'impact'        // a minion dies in combat / a card lands on the board
  | 'sell'          // a card is sold (or transferred to a COOP teammate)
  | 'beast'         // swarmers spawn / an Undead is played or spawned
  | 'apply'         // a card is bought from the shop
  | 'upgrade'       // a minion is transformed into another class
  | 'playCard';     // any spell is cast

/** The sound file for each name (public/Sounds/ — served at /Sounds/…). */
export const SOUND_FILES: Record<SoundName, string> = {
  btnClick: 'BtnClick.wav',
  clockTic: 'ClockTic.wav',
  explode: 'Explode.wav',
  powerUp: 'PowerUp.wav',
  attack3: 'Attack_3.wav',
  attack2: 'Attack_2.wav',
  buyRune: 'BuyRune.wav',
  shieldImpact: 'ShieldImpact.wav',
  impact: 'Impact.wav',
  sell: 'Sell.wav',
  beast: 'BeastSound.wav',
  apply: 'Apply.wav',
  upgrade: 'Uppgrade.wav',
  playCard: 'PlayCard.wav',
};

/** localStorage key for the sound settings (muted + volume). */
const SETTINGS_KEY = 'project-x-sound-settings';

/**
 * Concurrent voices per sound: the pool lets a sound overlap with itself
 * (double hits / twin booms) while keeping the total element count small
 * (14 sounds × 3 = 42 elements, all shared after first play).
 */
const POOL_SIZE = 3;

interface SoundSettings {
  muted: boolean;
  volume: number; // 0..1
}

function loadSettings(): SoundSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { muted: false, volume: 1 };
    const parsed = JSON.parse(raw) as Partial<SoundSettings>;
    return {
      muted: parsed.muted === true,
      volume: typeof parsed.volume === 'number' && isFinite(parsed.volume)
        ? Math.min(1, Math.max(0, parsed.volume))
        : 1,
    };
  } catch {
    return { muted: false, volume: 1 };
  }
}

export class SoundService {
  private _muted: boolean;
  private _volume: number;
  private _pools = new Map<SoundName, HTMLAudioElement[]>();
  private _cursors = new Map<SoundName, number>();

  constructor() {
    const saved = loadSettings();
    this._muted = saved.muted;
    this._volume = saved.volume;
  }

  get muted(): boolean { return this._muted; }
  get volume(): number { return this._volume; }

  /** Settings page: the mute button. Persists + silences all pooled voices. */
  setMuted(muted: boolean): void {
    this._muted = muted;
    this._persist();
  }

  /** Settings page: the volume slider (0..1). Persists + applies live. */
  setVolume(volume: number): void {
    this._volume = Math.min(1, Math.max(0, volume));
    for (const pool of this._pools.values()) {
      for (const el of pool) el.volume = this._volume;
    }
    this._persist();
  }

  private _persist(): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({
        muted: this._muted,
        volume: this._volume,
      }));
    } catch {
      // Storage unavailable (private mode) — the settings just don't persist.
    }
  }

  /**
   * THE single playback gate (mute + volume live here). Safe to call for
   * any name at any time — no-ops when muted, before the first play, or in
   * environments without Audio. Never throws (autoplay-policy rejects are
   * swallowed: a blocked play simply stays silent).
   */
  play(name: SoundName): void {
    if (this._muted) return;
    if (typeof Audio === 'undefined') return;
    let pool = this._pools.get(name);
    if (!pool) {
      pool = [];
      for (let i = 0; i < POOL_SIZE; i++) {
        const el = new Audio(`Sounds/${SOUND_FILES[name]}`);
        el.volume = this._volume;
        // preload=auto: the browser fetches (and then CACHES) the file so
        // replays after the first play are instant.
        el.preload = 'auto';
        pool.push(el);
      }
      this._pools.set(name, pool);
      this._cursors.set(name, 0);
    }
    // Round-robin: overlapping plays of the same sound take different
    // voices instead of restarting each other.
    const cursor = this._cursors.get(name) ?? 0;
    const el = pool[cursor % pool.length];
    this._cursors.set(name, cursor + 1);
    try {
      el.currentTime = 0;
      el.play().catch(() => {
        // Autoplay policy / decode hiccup — stay silent, never break the
        // game flow.
      });
    } catch {
      // Same as above (older engines throw instead of rejecting).
    }
  }
}

/** The app-wide singleton (main.ts wires the Settings UI + every trigger). */
export const soundService = new SoundService();
